import type { ReactNode } from 'react';
import { NULL_VALUE } from 'constants/displayConstants';
import {
  useOrgEventAutomationRules,
  type OrgEventAutomationRule,
} from 'hooks/orgEvents/orgEventAutomationHooks';
import type { OrgEvent } from 'hooks/orgEvents/orgEventHooks';

function formatEventTime(value: string | null | undefined): string {
  if (!value) return NULL_VALUE;
  try {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(new Date(value));
  } catch {
    return NULL_VALUE;
  }
}

function normalize(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase();
}

/** Best automation rule that likely created this event. */
export function triggeredRuleForEvent(
  rules: OrgEventAutomationRule[] | undefined,
  event: Pick<
    OrgEvent,
    'type' | 'name' | 'threshold_description' | 'demo_key' | 'region'
  > & { creation_kind?: string | null }
): OrgEventAutomationRule | undefined {
  if (event.creation_kind === 'user') return undefined;
  const active = (rules ?? []).filter((rule) => !rule.archived);
  if (active.length === 0) return undefined;

  const type = normalize(event.type);
  const typed = type
    ? active.filter((rule) => normalize(rule.event_template?.type) === type)
    : [];
  const pool = typed.length > 0 ? typed : active;

  const eventTokens = [
    event.demo_key,
    event.region,
    event.name,
    event.threshold_description,
  ]
    .map(normalize)
    .filter(Boolean);

  const scored = pool
    .map((rule) => {
      const hay = normalize(
        `${rule.name} ${rule.event_template?.thresholdDescription ?? ''} ${rule.event_template?.region ?? ''}`
      );
      let score = 0;
      for (const token of eventTokens) {
        if (token.length >= 3 && hay.includes(token)) score += 2;
        const words = token.split(/[^a-z0-9]+/).filter((w) => w.length >= 3);
        for (const word of words) {
          if (hay.includes(word)) score += 1;
        }
      }
      return { rule, score };
    })
    .sort((a, b) => b.score - a.score);

  if (scored[0] && scored[0].score > 0) return scored[0].rule;
  return pool[0];
}

export const EVENT_TRIGGER_LABEL = 'Trigger';
export const EVENT_LAST_UPDATED_LABEL = 'Last Updated';
export const PRATUS_ACTOR = 'PRATUS';

export function eventStartedByLabel(
  event: Pick<
    OrgEvent,
    'creation_kind' | 'created_by_name' | 'created_by_user_guid'
  >
): string | null {
  if (event.creation_kind !== 'user') return PRATUS_ACTOR;
  const name = event.created_by_name?.trim();
  if (name) return name;
  return event.created_by_user_guid ? null : PRATUS_ACTOR;
}

export function eventLastUpdatedByLabel(
  event: Pick<OrgEvent, 'updated_by_user_guid' | 'updated_by_name'>
): string | null {
  if (!event.updated_by_user_guid) return PRATUS_ACTOR;
  const name = event.updated_by_name?.trim();
  return name || null;
}

function formatAttributedTime(
  value: string | null | undefined,
  actor: string | null
): string {
  const formatted = formatEventTime(value);
  if (formatted === NULL_VALUE || !actor) return formatted;
  return `${formatted} by ${actor}`;
}

/** Trigger shows the matched rule name, else the stored threshold text. */
export function eventTriggerValue(
  event: Pick<OrgEvent, 'threshold_description'> &
    Partial<Pick<OrgEvent, 'creation_kind' | 'created_by_name' | 'created_at'>>,
  triggeredRule: OrgEventAutomationRule | undefined
): string | null {
  if (event.creation_kind === 'user') {
    return manualEventTrigger(event);
  }
  const ruleName = triggeredRule?.name?.trim();
  if (ruleName) return ruleName;
  const threshold = event.threshold_description?.trim();
  return threshold || null;
}

function manualEventTrigger(
  event: Partial<Pick<OrgEvent, 'created_by_name' | 'created_at'>>
): string | null {
  const name = event.created_by_name?.trim();
  const formatted = formatEventTime(event.created_at);
  if (name && formatted !== NULL_VALUE) {
    return `created by ${name} at ${formatted}`;
  }
  if (name) return `created by ${name}`;
  if (formatted !== NULL_VALUE) return `created at ${formatted}`;
  return null;
}

function DetailRow({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <p className="tw:m-0 tw:text-foreground">
      <span className="tw:font-medium">{label}:</span> {children}
    </p>
  );
}

type OrgEventDetailsBodyProps = {
  event: OrgEvent;
  onTriggerRuleClick?: (rule: OrgEventAutomationRule) => void;
};

/** Shared event detail fields for Events list expand + preview. */
export function OrgEventDetailsBody({
  event,
  onTriggerRuleClick,
}: OrgEventDetailsBodyProps) {
  const { data: rules } = useOrgEventAutomationRules();
  const triggeredRule = triggeredRuleForEvent(rules, event);
  const triggerValue = eventTriggerValue(event, triggeredRule);
  const canOpenTrigger = !!triggeredRule && !!onTriggerRuleClick;

  return (
    <div className="tw:space-y-1.5 tw:text-xs tw:text-foreground">
      <DetailRow label="Summary">{event.summary || NULL_VALUE}</DetailRow>
      {triggerValue ? (
        <DetailRow label={EVENT_TRIGGER_LABEL}>
          {canOpenTrigger ? (
            <button
              type="button"
              className="tw:bg-transparent tw:border-0 tw:p-0 tw:font-medium tw:text-primary tw:underline tw:underline-offset-2 tw:cursor-pointer"
              onClick={() => onTriggerRuleClick(triggeredRule)}
            >
              {triggerValue}
            </button>
          ) : (
            triggerValue
          )}
        </DetailRow>
      ) : null}
      <DetailRow label="Started">
        {formatAttributedTime(event.started_at, eventStartedByLabel(event))}
      </DetailRow>
      <DetailRow label={EVENT_LAST_UPDATED_LABEL}>
        {formatAttributedTime(
          event.last_update,
          eventLastUpdatedByLabel(event)
        )}
      </DetailRow>
    </div>
  );
}
